import moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import Box from 'web-makeup/dist/Box'
import Button from 'web-makeup/dist/Button'
import DatePicker from 'web-makeup/dist/DatePicker'
import Form from 'web-makeup/dist/Form'
import H5 from 'web-makeup/dist/H5'
import Input from 'web-makeup/dist/Input'
import Select from 'web-makeup/dist/Select'
import { getLocaleFromBrowserLanguage } from 'web-fns/dist/language'
import { SelectValue } from 'antd/lib/select'
import { FormComponentProps } from 'antd/lib/form'

import { GetProducerProfile } from '../../typings/types'
import AvatarUpload from '../AvatarUpload/AvatarUpload'
import LocationAutoComplete from '../LocationAutoComplete/LocationAutoComplete'

import CloudinaryContext from './../CloudinaryContext'
import GoogleContext from './../GoogleContext'

const Icon = require('antd/lib/icon')
const Option = Select.Option

const FormItem = styled(Form.Item as any)`
  &&& {
    margin-bottom: 0;
  }

  &::before,
  &::after {
    display: none;
  }

  .ant-form-item-control {
    line-height: 1;
  }

  .ant-input-group-wrapper {
    top: 0;
  }

  .ant-form-item-control {
    &::before,
    &::after {
      display: none;
    }
  }

  .ant-form-explain {
    margin-top: 0.125rem;
  }
`

const StyledBox = styled(Box)`
  border: none;

  @media (min-width: 40rem) {
    border-left: none;
    border-right: none;
  }
`

export const StyledForm = styled(Form as any)`
  display: grid;
  grid-gap: 1rem;
  margin-top: 1.5rem;

  @media all and (min-width: 32rem) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const SubmitButton = styled(Button as any)`
  &&& {
    width: 100%;
    height: 3rem;
  }

  > i {
    margin-right: 0.5rem;
  }
`

const Holder = styled.div`
  display: grid;
  grid-gap: 1rem;

  @media all and (min-width: 32rem) {
    grid-column: span 2;
    grid-template-columns: auto 1fr;
  }
`

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`

const UploadItem = styled(FormItem)`
  &&& {
    margin: 0 auto;
  }

  .ant-upload.ant-upload-select-picture-card {
    margin: 0;

    .ant-upload {
      padding: 0.25rem;
    }
  }

  @media all and (min-width: 40rem) {
    grid-column: 1;
    grid-row: 1 / 4;
  }
`

const StyledIcon = styled(Icon)`
  color: #d8d8d8;
`

const Footer = styled.div`
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: ${(props: { isBlocked: string }) =>
    props.isBlocked ? `auto` : `100% 100%`};
  align-items: center;
  ${(props: { isBlocked: string }) => props.isBlocked && `grid-column: 1 / span 2;`};
`

const ToggleBlockButton = styled(Button as any)`
  &&& {
    width: 100%;
    height: 3rem;
  }

  > i {
    margin-right: 0.5rem;
  }
`

interface ProducerProfileProps {
  producer: GetProducerProfile.GetProducerProfile
  onFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  handleLogoUploadSuccess: (cloudinaryId: string, uploadFor: string) => void
  handleLogoUploadError: (uploadFor: string) => void
  handleLocationSelect: (value?: SelectValue, options?: object) => void
  handleLocationChange: (value?: SelectValue) => void
  onUnblock: () => void
  onBlock: () => void
  updating: boolean
  blocking: boolean
  unblocking: boolean
}

export const getAvatarUrl = (producer: GetProducerProfile.User, url) => {
  if (!producer || !producer.avatar) {
    return `${url}/upload/placeholders/icon-user.png`
  }
  if (producer.avatar.cloudinaryType === 'fetch' && producer.avatar.cloudinaryId) {
    return decodeURIComponent(producer.avatar.cloudinaryId)
  }
  if (producer.avatar.cloudinaryType === 'upload') {
    return `${url}/${producer.avatar.cloudinaryType}/${producer.avatar.cloudinaryId}.png`
  }
  return `${url}/upload/placeholders/icon-user.png`
}

export default ({
  form: { getFieldDecorator },
  producer,
  onFormSubmit,
  handleLogoUploadSuccess,
  handleLogoUploadError,
  handleLocationSelect,
  handleLocationChange,
  updating,
  blocking,
  unblocking,
  onBlock,
  onUnblock,
}: ProducerProfileProps & FormComponentProps) => (
  <StyledBox>
    <H5>Personal details</H5>
    <StyledForm onSubmit={onFormSubmit} data-qa="producer-account-form">
      <Holder>
        <CloudinaryContext.Consumer>
          {({ UPLOAD_PRESET, UPLOAD_URL, DOWNLOAD_URL }) => (
            <UploadItem>
              <AvatarUpload
                action={UPLOAD_URL}
                uploadPreset={UPLOAD_PRESET}
                initialImage={getAvatarUrl(producer && producer.user, DOWNLOAD_URL)}
                onSuccess={cloudinaryId => handleLogoUploadSuccess(cloudinaryId, 'user')}
                onError={() => handleLogoUploadError('user')}
                message="Upload Avatar"
              />
            </UploadItem>
          )}
        </CloudinaryContext.Consumer>
        {getFieldDecorator('user.avatar')(<Input type="hidden" />)}

        <FormItem>
          <Input
            disabled
            data-qa="account-email"
            value={producer && producer.user.email}
            prefix={<StyledIcon type="mail" />}
          />
        </FormItem>
        <FormItem>
          {getFieldDecorator('user.name', {
            initialValue: producer && producer.user && producer.user.name,
            rules: [
              {
                required: true,
                message: 'Name is required',
              },
            ],
          })(
            <Input
              data-qa="account-profile-name"
              prefix={<StyledIcon type="user" />}
              placeholder="Name"
            />
          )}
        </FormItem>

        <GoogleContext.Consumer>
          {({ MAPS_API_KEY }) => (
            <FormItem>
              {getFieldDecorator('user.location', {
                // @ts-ignore Not sure why this is here, can't see it in the docs
                hidden: true,
                initialValue:
                  producer &&
                  producer.user &&
                  producer.user.location &&
                  `${producer.user.location.city}, ${producer.user.location.country}`,
              })(
                <LocationAutoComplete
                  apiKey={MAPS_API_KEY}
                  language={getLocaleFromBrowserLanguage(window.navigator)}
                  onSelect={handleLocationSelect}
                  onChange={handleLocationChange}
                  placeholder="Location"
                  placeTypes={['(cities)']}
                />
              )}
            </FormItem>
          )}
        </GoogleContext.Consumer>
        {getFieldDecorator('user.googlePlaceId', {
          initialValue:
            producer &&
            producer.user &&
            producer.user.location &&
            producer.user.location.googlePlaceId,
        })(<Input type="hidden" />)}
      </Holder>

      <FormItem>
        {getFieldDecorator('user.phoneNumber', {
          initialValue: producer && producer.user && producer.user.phoneNumber,
        })(
          <Input
            data-qa="edit-profile-phone"
            prefix={<StyledIcon type="tablet" />}
            placeholder="Phone Number"
          />
        )}
      </FormItem>
      <FormItem>
        {getFieldDecorator('user.skypeUsername', {
          initialValue: producer && producer.user && producer.user.skypeUsername,
        })(
          <Input
            data-qa="account-profile-skype"
            prefix={<StyledIcon type="skype" />}
            placeholder="Skype Username"
          />
        )}
      </FormItem>
      <FormItem>
        {getFieldDecorator('user.dateOfBirth', {
          initialValue:
            producer &&
            producer.user &&
            producer.user.dateOfBirth &&
            moment(producer.user.dateOfBirth),
        })(
          <StyledDatePicker className="account-profile-data-of-birth" placeholder="Date of birth" />
        )}
      </FormItem>
      <FormItem>
        {getFieldDecorator('user.gender', {
          initialValue:
            producer && producer.user && producer.user.gender ? producer.user.gender : undefined,
        })(
          <Select className="account-profile-gender" placeholder="Gender">
            <Option value="MALE">Male</Option>
            <Option value="FEMALE">Female</Option>
          </Select>
        )}
      </FormItem>
      <Footer isBlocked={producer && producer.blockedAt}>
        {producer &&
          !producer.blockedAt && (
            <SubmitButton
              type="primary"
              size="large"
              data-qa="update-cta"
              htmlType="submit"
              loading={updating}
            >
              Update
            </SubmitButton>
          )}
        {producer &&
          producer.activated &&
          producer.blockedAt && (
            <ToggleBlockButton
              type="primary"
              size="large"
              htmlType="button"
              loading={unblocking}
              onClick={onUnblock}
            >
              Unblock
            </ToggleBlockButton>
          )}
        {producer &&
          producer.activated &&
          !producer.blockedAt && (
            <ToggleBlockButton
              type="secondary"
              size="large"
              htmlType="button"
              loading={blocking}
              onClick={onBlock}
            >
              Block
            </ToggleBlockButton>
          )}
      </Footer>
    </StyledForm>
  </StyledBox>
)



// WEBPACK FOOTER //
// ./src/components/ProducerProfile/ProducerProfile.tsx